import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { sessionKey } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { useAvailability } from '../lib/availability';
import { NewPasswordField } from '../components/PasswordField';
import { Alert, Button, Field } from '../components/ui';

export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const send = useMutation({ mutationFn: () => api.post('/auth/password/forgot', { email }) });
  const fields = fieldErrors(send.error);
  const availability = useAvailability();

  if (availability.data && !availability.data.passwordResets) {
    return (
      <AuthLayout title="Reset your password" description="Password resets need email, which isn’t switched on yet.">
        <p className="text-muted">
          Email <a href="mailto:hello@hoopify.com.au" className="text-action hover:underline">hello@hoopify.com.au</a> and we’ll help you get back in.
        </p>
      </AuthLayout>
    );
  }

  if (send.isSuccess) {
    return (
      <AuthLayout
        title="Check your email"
        description={
          <>
            If there’s an account for <strong className="font-semibold text-ink">{email}</strong>, we’ve sent it a link to choose a new password. The link works for 1 hour.
          </>
        }
      >
        <Link to="/login" className="btn-secondary w-full">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter the email you sign in with and we’ll send you a link to choose a new password."
      footer={
        <Link to="/login" className="font-medium text-action hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); send.mutate(); }} className="space-y-4" noValidate>
        {send.isError && !fields.email && <Alert>{errorMessage(send.error)}</Alert>}
        <Field label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} required autoFocus />
        <Button type="submit" loading={send.isPending} className="btn-primary btn-lg w-full">
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: () => api.post('/auth/password/reset', { token, password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
  const fields = fieldErrors(reset.error);

  if (reset.isSuccess) {
    return (
      <AuthLayout title="Password changed" description="You’ve been signed out everywhere. Sign in with your new password.">
        <Link to="/login" className="btn-primary btn-lg w-full">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); reset.mutate(); }} className="space-y-4" noValidate>
        {!token && <Alert>This link is incomplete. Copy the whole link from the email into your browser.</Alert>}
        {reset.isError && !fields.password && (
          <Alert>
            {errorMessage(reset.error)}{' '}
            <Link to="/forgot-password" className="font-medium underline">
              Send a new link
            </Link>
          </Alert>
        )}
        <NewPasswordField label="New password" value={password} onChange={setPassword} error={fields.password} userInputs={[]} />
        <Button type="submit" loading={reset.isPending} disabled={!token} className="btn-primary btn-lg w-full">
          Save new password
        </Button>
      </form>
    </AuthLayout>
  );
}
